import { Modal } from "antd";
import AlertContent from "../components/AlertContent";

/**
 * 2개의 Object의 일치여부 반환 함수
 *
 * @author 최하영
 * @since 2023-12-01
 * @param { Object } obj1
 * @param { Object } obj2
 * @returns { Boolean } 일치여부
 */
export const areObjectsEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        const value1 = obj1[key]
        const value2 = obj2[key]

        if (typeof value1 === 'object' && typeof value2 === 'object' && value1 && value2) {
            // 재귀적으로 객체 내용을 비교
            if (!areObjectsEqual(value1, value2)) {
                return false;
            }
        } else if (value1 !== value2) {
            return false;
        }
    }

    return true;
}

/**
 * 파일을 base64 형식으로 변환하는 함수
 * 
 * @author 최하영
 * @since 2024-01-18
 * @param {File} file - 변환할 파일 객체
 * @returns {Promise<string>} - 변환된 base64 문자열을 담은 프로미스 객체
 */
export const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};


/**
 * 현재 페이지, 총 페이지 수로 데이터를 페이징처리하여 반환하는 함수
 * 
 * @author 최하영
 * @since 2024-03-18
 * @param {Array} data - 페이징 처리할 데이터
 * @param {Number} currentPage - 현재 페이지
 * @param {Number} pageSize - 표시할 데이터 수
 * @returns {Array} - 페이지에 표시할 데이터 배열
 */
export const paginateArray = (data, currentPage, pageSize) => {
    const newOffset = (currentPage - 1) * pageSize;
    return data.rows.slice(newOffset, newOffset + pageSize);
};

/**
 * 행 데이터 객체의 순서를 columnDefs 순서에 따라 정렬하여 반환하는 함수
 * 
 * @author 최하영
 * @since 2024-03-19
 * @param {Object} rowData - 정렬할 객체
 * @param {Array} columnDefs - 정렬된 배열
 * @returns {Object} - 정렬된 객체
 */
export const mapRowDataToColumnDefs = (rowData, columnDefs) => {
    const mappedData = {};
    columnDefs.forEach(column => {
      mappedData[column.field] = rowData[column.field];
    });
    return mappedData;
}

/**
    * 엑셀파일 등록 시 오류사항이 있을 경우 안내 모달을 띄운다.
    * 
    * @author 최하영
    * @since 2024-05-21
    * @param {Object} res 응답데이터
    * @param {Function} callback 콜백함수
 */
export const createFailModal = (res, callback) => {
    let msg = ''
    if(res.total === 0) {
        msg = '등록할 데이터가 없습니다.\n엑셀 파일에 첨부된 작성 요령에 맞게 작성 후 업로드 해주세요.'
    } else if(res.available === 0) {
        msg = `작성하신 내용 중 양식과 맞지 않는 내용이 있습니다.\n엑셀 파일에 첨부된 작성요령을 다시 한 번 확인하신 후 업로드 해주세요.`
    } else {
        msg = `요청하신 ${res.total}개의 데이터 중 ${res.available}개가 성공적으로 등록되었습니다.\n
        ${res.total - res.available}개의 데이터는 엑셀 파일에 첨부된 작성요령을\n다시 한 번 확인하신 후 업로드 해주세요.`
    }

    const config = {
        icon: <></>,
        content: <AlertContent title='WARNING'>
                { msg }
            </AlertContent>,
        style: { wordBreak: 'break-all', whiteSpace: 'pre-line'},
        onOk: callback,
        centered: true,
    };
    return Modal.warning(config)
}


/**
    * 엑셀파일 등록 시 서버로부터 전달받은 중복학생 리스트, null값 체크리스트로
    * dataGrid Modal을 띄운다.
    * 
    * @author 최하영
    * @since 2024-05-21
    * @param {Array} unavailableAsValidation 유효하지 않은 데이터가 있는 수검자 리스트
 */
export const createFailListData = ( unavailableAsValidation ) => {
    if( unavailableAsValidation.length === 0 ) return 

    function getFailReason(data) {
      let failReason = [];
      Object.keys(data.fail).length > 0 && failReason.push('형식 오류')
      Object.keys(data.duplication).length > 0 && failReason.push('사용자 중복')

      return failReason.length > 1 ? failReason.join(', ') : failReason[0]
    }
    // 실패사유가 형식오류인 경우 어떤게 오류인건지 표시
    const unavailableList = unavailableAsValidation.map((data) => {
      const result = {};
      Object.entries(data.fail).forEach(([key, value]) => {
        result[key] = `fail_${value}`;
      });
      Object.entries(data.duplication).forEach(([key, value]) => {
        result[key] = `dup_${value}`;
      });
    
      return  { ...result, ...data.success, failReason: getFailReason(data)};
    });

    console.log('unavailableList', unavailableList)
    return [...unavailableList]
}
/**
    * 엑셀파일 등록 실패 리스트 모달의 cell 데이터에서 
    * 구분을 위해 추가한 fail_, dup_ 문구를 제거한다.
    * 
    * @author 최하영
    * @since 2024-05-22
    * @param {String} value
 */
export const removePrefix = (value) => {
    if (value === null) return '' 
    if (value && value.startsWith('fail_')) {
        return value.slice(5) === 'null' ? '' : value.slice(5);
    }
    if (value && value.startsWith('dup_')) {
        return value.slice(4) === 'null' ? '' : value.slice(4);
    }
    return value;
};

/**
    * 상대경로의 파일을 다운로드 받는다.
    * 
    * @author 최하영
    * @since 2024-05-28
    * @param {String} url 파일 경로
    * @param {String} fileName 파일명
 */
export const downloadFile = ({url, fileName}) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank'; // 새로운 탭에서 열기

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};


// 브랜드에 따른 파비콘, 타이틀 설정값 상수화
export const BRAND_CONFIG = {
  SMART_BALANCE: {
    // 환경 변수가 없을 경우를 대비한 기본값 추가
    favicon: process.env.REACT_APP_SMART_BALANCE_FAVICON || '/favicon-sb.ico',
    title: process.env.REACT_APP_SMART_BALANCE_TITLE || 'Smart Balance',
  },
  MOTIONCORE: {
    favicon: process.env.REACT_APP_MOTIONCORE_FAVICON || '/favicon-mc.ico',
    title: process.env.REACT_APP_MOTIONCORE_TITLE || 'MOTIONCORE',
  },
};


/**
 * centerClassCode 또는 접속 도메인에 따라 브랜드를 결정 
 *
 * @author kh.kang
 * @since 2025-04-17
 * @param {string | null | undefined} centerClassCode
 * @param {string} hostname 현재 접속 도메인
 * @returns {'MOTIONCORE' | 'SMART_BALANCE'} 헬스케어에서 서비스하는 브랜드 이름
 */
export const determineBrand = (centerClassCode, hostname) => {
  // 1. centerClassCode 우선 확인
  if (centerClassCode === 'S' || centerClassCode === 'F') {
    return 'SMART_BALANCE';
  }
  // centerClassCode가 있지만 S/F가 아니면 MOTIONCORE
  if (centerClassCode) {
    return 'MOTIONCORE';
  }

  // 2. centerClassCode가 없으면 hostname 확인
  if (hostname === process.env.REACT_APP_SMART_BALANCE_DOMAIN) {
    return 'SMART_BALANCE';
  }

  // 3. 기본값은 MOTIONCORE
  return 'MOTIONCORE';
};


/**
 * 결정된 브랜드 설정 (파비콘, 타이틀)을 DOM에 적용
 *
 * @author kh.kang
 * @since 2025-04-17
 * @param {object} brandAssets - { favicon: string, title: string } 형태의 객체
 */
const applyBrandAssetsToDOM = (brandAssets) => {
  const { favicon, title } = brandAssets;
  const cacheBuster = `?v=${new Date().getTime()}`; // 파비콘 캐시 무효화

  // 타이틀 설정
  document.title = title;

  // 파비콘 설정
  const faviconLink = document.querySelector("link[rel~='icon']");
  if (faviconLink && favicon) {
    // PUBLIC_URL을 사용하여 절대 경로 생성 (CRA 표준 방식)
    faviconLink.href = `${process.env.PUBLIC_URL}${favicon}${cacheBuster}`;
  }  
};


/**
 * 접속 환경(centerClassCode 또는 도메인)에 따라 파비콘과 타이틀을 설정
 * GeneralLayout.jsx에서 호출 됨
 * 
 * @author kh.kang
 * @since 2025-04-15
 * @param {string | null | undefined} centerClassCode
 */
export const setFaviconTitle = (centerClassCode) => {
  const hostname = window.location.hostname;
  const brand = determineBrand(centerClassCode, hostname);
  const brandAssets = BRAND_CONFIG[brand];

  if (brandAssets) {
    applyBrandAssetsToDOM(brandAssets);
  } else {
    console.error(`Brand assets not found for brand: ${brand}`);
    // 기본값 적용
    applyBrandAssetsToDOM(BRAND_CONFIG.MOTIONCORE);
  }
};


/**
 * 중첩된 객체에서 문자열 경로를 사용하여 값을 가져오는 함수
 * 
 * @author kh.kang
 * @since 2025-09-29
 * @param {object} obj 값을 가져올 객체
 * @param {string} path dot으로 구분된 경로 (e.g., 'options.OPTION_PREMIUM')
 * @returns 경로에 해당하는 값 또는 undefined
 */
export function getValueFromNestedObject(obj, path) {
  return path.split('.').reduce((o, k) => (o || {})[k], obj);
}